import { Locale } from '../types/global';

export const ALL_TAG = 'all';

const API_BASE_URL = import.meta.env.VITE_PAYLOAD_API_URL ?? 'http://localhost:3000';
const ARTWORKS_ENDPOINT = `${API_BASE_URL}/api/artworks`;
const TAGS_ENDPOINT = `${API_BASE_URL}/api/tags`;

const APP_LOCALES: Locale[] = ['en', 'zh'];
const APP_TO_PAYLOAD_LOCALE: Record<Locale, string> = {
  en: 'en',
  zh: 'zh-CN'
};

export type MediaAsset = {
  id: number;
  url: string;
  alt: Record<Locale, string>;
};

export type ResolvedTag = {
  id: string;
  label: Record<Locale, string>;
};

export type AuthorSummary = {
  id: string;
  name: Record<Locale, string>;
};

export type GalleryItem = {
  id: string;
  slug: string;
  title: Record<Locale, string>;
  description: Record<Locale, string>;
  year: number;
  tags: ResolvedTag[];
  media: MediaAsset[];
  author: AuthorSummary | null;
};

export type GalleryFilterOptions = {
  tagIds?: string[];
};

type PayloadLexicalNode = {
  type: string;
  text?: string;
  children?: PayloadLexicalNode[];
};

type PayloadRichText = {
  root?: PayloadLexicalNode;
};

type PayloadMedia = {
  id: number;
  url: string;
  alt?: string;
};

type PayloadTag = {
  id: number | string;
  name?: string | null;
};

type PayloadAuthorRelationship = {
  id: number | string;
  name?: string | null;
};

type PayloadArtworkLocale = {
  id: number;
  title?: string | null;
  description?: PayloadRichText | null;
  year?: number | null;
  photos?: PayloadMedia[];
  author?: PayloadAuthorRelationship | number | string | null;
  tags?: Array<PayloadTag | number | string | null> | null;
};

type ArtworkListResponseLocale = {
  docs: PayloadArtworkLocale[];
  nextPage?: number | null;
  hasNextPage?: boolean;
};

type TagListResponseLocale = {
  docs: PayloadTag[];
  nextPage?: number | null;
  hasNextPage?: boolean;
};

const buildMediaUrl = (path: string): string => {
  try {
    return new URL(path, API_BASE_URL).toString();
  } catch (error) {
    return path;
  }
};

const flattenLexicalNode = (node?: PayloadLexicalNode): string => {
  if (!node) {
    return '';
  }

  if (typeof node.text === 'string') {
    return node.text;
  }

  if (!node.children) {
    return '';
  }

  return node.children.map((child) => flattenLexicalNode(child)).join(' ').trim();
};

const richTextToPlainText = (value?: PayloadRichText | null): string => {
  if (!value?.root) {
    return '';
  }
  return flattenLexicalNode(value.root);
};

type TagEntry = {
  id: string;
  name: string;
};

const extractTagEntries = (tags?: Array<PayloadTag | number | string | null> | null): TagEntry[] => {
  if (!Array.isArray(tags)) {
    return [];
  }

  return tags
    .map((tag) => {
      if (tag == null) {
        return null;
      }

      if (typeof tag === 'number' || typeof tag === 'string') {
        return { id: String(tag), name: '' } satisfies TagEntry;
      }

      if (typeof tag === 'object' && 'id' in tag) {
        const value = tag as PayloadTag;
        if (value.id == null) {
          return null;
        }
        return {
          id: String(value.id),
          name: typeof value.name === 'string' ? value.name : ''
        } satisfies TagEntry;
      }

      return null;
    })
    .filter((entry): entry is TagEntry => entry !== null);
};

const applyTagsToMap = (map: Map<string, ResolvedTag>, entries: TagEntry[], locale: Locale) => {
  entries.forEach(({ id, name }) => {
    const existing = map.get(id);
    const trimmed = name ?? '';

    if (existing) {
      if (trimmed) {
        existing.label = {
          ...existing.label,
          [locale]: trimmed
        };
      }
      return;
    }

    map.set(id, {
      id,
      label: {
        en: locale === 'en' ? trimmed : '',
        zh: locale === 'zh' ? trimmed : ''
      }
    });
  });
};

const finalizeTags = (map: Map<string, ResolvedTag>): ResolvedTag[] =>
  Array.from(map.values()).map((tag) => ({
    ...tag,
    label: {
      en: tag.label.en,
      zh: tag.label.zh || tag.label.en
    }
  }));

type AuthorEntry = {
  id: string;
  name: string;
};

const extractAuthorEntry = (author: PayloadArtworkLocale['author']): AuthorEntry | null => {
  if (!author) {
    return null;
  }

  if (typeof author === 'number' || typeof author === 'string') {
    return { id: String(author), name: '' } satisfies AuthorEntry;
  }

  if (typeof author === 'object' && 'id' in author) {
    const value = author as PayloadAuthorRelationship;
    if (value.id == null) {
      return null;
    }

    return {
      id: String(value.id),
      name: typeof value.name === 'string' ? value.name : ''
    } satisfies AuthorEntry;
  }

  return null;
};

const applyAuthorEntry = (
  current: AuthorSummary | null,
  entry: AuthorEntry | null,
  locale: Locale
): AuthorSummary | null => {
  if (!entry) {
    return current;
  }

  const base: AuthorSummary = current && current.id === entry.id
    ? { ...current, name: { ...current.name } }
    : {
        id: entry.id,
        name: {
          en: '',
          zh: ''
        }
      };

  const trimmed = entry.name.trim();

  if (trimmed) {
    base.name[locale] = trimmed;
    if (locale === 'en' && !base.name.zh) {
      base.name.zh = trimmed;
    }
  }

  return base;
};

const finalizeAuthor = (author: AuthorSummary | null): AuthorSummary | null => {
  if (!author) {
    return null;
  }

  return {
    ...author,
    name: {
      en: author.name.en,
      zh: author.name.zh || author.name.en
    }
  };
};

const normalizeMedia = (photos?: PayloadMedia[]): MediaAsset[] => {
  if (!photos) {
    return [];
  }

  return photos.map((photo) => ({
    id: photo.id,
    url: buildMediaUrl(photo.url),
    alt: {
      en: photo.alt ?? '',
      zh: ''
    }
  }));
};

type LocalePageResult = {
  docs: PayloadArtworkLocale[];
  nextPage: number | null;
  hasNextPage: boolean;
};

type LocaleTagPageResult = {
  docs: PayloadTag[];
  nextPage: number | null;
  hasNextPage: boolean;
};

const fetchArtworksForLocale = async (
  locale: Locale,
  page: number,
  limit: number,
  filters: GalleryFilterOptions
): Promise<LocalePageResult> => {
  const payloadLocale = APP_TO_PAYLOAD_LOCALE[locale];
  const params = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    locale: payloadLocale,
    depth: '1'
  });

  const tagIds = filters.tagIds?.filter(Boolean);
  if (tagIds && tagIds.length > 0) {
    const unique = Array.from(new Set(tagIds.map((tag) => String(tag))));
    if (unique.length === 1) {
      params.append('where[tags][contains]', unique[0]);
    } else {
      params.append('where[tags][in]', unique.join(','));
    }
  }

  const response = await fetch(`${ARTWORKS_ENDPOINT}?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to load artworks (${locale}): ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as ArtworkListResponseLocale;
  return {
    docs: data.docs,
    nextPage: data.nextPage ?? null,
    hasNextPage: Boolean(data.hasNextPage ?? (data.nextPage ?? null) !== null)
  };
};

const fetchTagsForLocale = async (
  locale: Locale,
  page: number,
  limit: number
): Promise<LocaleTagPageResult> => {
  const payloadLocale = APP_TO_PAYLOAD_LOCALE[locale];
  const params = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    locale: payloadLocale
  });

  const response = await fetch(`${TAGS_ENDPOINT}?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to load tags (${locale}): ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as TagListResponseLocale;

  return {
    docs: data.docs,
    nextPage: data.nextPage ?? null,
    hasNextPage: Boolean(data.hasNextPage ?? (data.nextPage ?? null) !== null)
  };
};

type ItemAccumulator = {
  item: GalleryItem;
  tagMap: Map<string, ResolvedTag>;
  author: AuthorSummary | null;
};

const mergeLocales = (localeData: Record<Locale, PayloadArtworkLocale[]>): GalleryItem[] => {
  const englishDocs = localeData.en;
  const byId = new Map<number, ItemAccumulator>();

  englishDocs.forEach((doc) => {
    const tagMap = new Map<string, ResolvedTag>();
    applyTagsToMap(tagMap, extractTagEntries(doc.tags ?? null), 'en');

    const authorSummary = applyAuthorEntry(null, extractAuthorEntry(doc.author), 'en');

    byId.set(doc.id, {
      item: {
        id: String(doc.id),
        slug: String(doc.id),
        title: { en: doc.title ?? '', zh: '' },
        description: { en: richTextToPlainText(doc.description), zh: '' },
        year: doc.year ?? 0,
        tags: [],
        media: normalizeMedia(doc.photos),
        author: finalizeAuthor(authorSummary)
      },
      tagMap,
      author: authorSummary
    });
  });

  for (const locale of APP_LOCALES) {
    if (locale === 'en') {
      continue;
    }

    const docs = localeData[locale];
    docs.forEach((doc) => {
      const entry = byId.get(doc.id);
      if (!entry) {
        return;
      }

      const { item, tagMap } = entry;
      item.title[locale] = doc.title ?? item.title.en;
      item.description[locale] = richTextToPlainText(doc.description) || item.description.en;

      if (doc.photos && doc.photos.length > 0) {
        const photoMap = new Map(doc.photos.map((photo) => [photo.id, photo] as const));
        item.media = item.media.map((media) => {
          const match = photoMap.get(media.id);
          if (match?.alt) {
            return {
              ...media,
              alt: {
                ...media.alt,
                [locale]: match.alt
              }
            };
          }
          return media;
        });
      }

      applyTagsToMap(tagMap, extractTagEntries(doc.tags ?? null), locale);
      entry.author = applyAuthorEntry(entry.author, extractAuthorEntry(doc.author), locale);
      item.author = finalizeAuthor(entry.author);
    });
  }

  return Array.from(byId.values()).map(({ item, tagMap }) => ({
    ...item,
    tags: finalizeTags(tagMap),
    media: item.media.map((media) => ({
      ...media,
      alt: {
        en: media.alt.en,
        zh: media.alt.zh || media.alt.en
      }
    })),
    author: finalizeAuthor(item.author)
  }));
};

export class GalleryNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GalleryNotFoundError';
  }
}

export type GalleryPageResult = {
  items: GalleryItem[];
  nextPage: number | null;
  hasNextPage: boolean;
};

export const loadGalleryPage = async (
  page = 1,
  limit = 20,
  filters: GalleryFilterOptions = {}
): Promise<GalleryPageResult> => {
  const englishResult = await fetchArtworksForLocale('en', page, limit, filters);

  const otherLocales = await Promise.all(
    APP_LOCALES.filter((locale) => locale !== 'en').map(async (locale) => ({
      locale,
      result: await fetchArtworksForLocale(locale, page, limit, filters)
    }))
  );

  const localeData: Record<Locale, PayloadArtworkLocale[]> = {
    en: englishResult.docs,
    zh: []
  };

  otherLocales.forEach(({ locale, result }) => {
    localeData[locale] = result.docs;
  });

  const items = mergeLocales(localeData).map((item) => ({
    ...item,
    media: item.media.map((media) => ({
      ...media,
      alt: {
        en: media.alt.en,
        zh: media.alt.zh || media.alt.en
      }
    }))
  }));

  return {
    items,
    nextPage: englishResult.nextPage,
    hasNextPage: englishResult.hasNextPage
  };
};

const fetchArtworkForLocale = async (slug: string, locale: Locale): Promise<PayloadArtworkLocale | null> => {
  const payloadLocale = APP_TO_PAYLOAD_LOCALE[locale];
  const response = await fetch(`${ARTWORKS_ENDPOINT}/${slug}?locale=${payloadLocale}&depth=1`);

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to load artwork ${slug} (${locale}): ${response.status} ${response.statusText}`);
  }

  return (await response.json()) as PayloadArtworkLocale;
};

export const loadGalleryItem = async (slug: string): Promise<GalleryItem> => {
  const localeResults = await Promise.all(
    APP_LOCALES.map(async (locale) => ({ locale, doc: await fetchArtworkForLocale(slug, locale) }))
  );

  const englishDoc = localeResults.find((entry) => entry.locale === 'en')?.doc;

  if (!englishDoc) {
    throw new GalleryNotFoundError(`Artwork ${slug} not found`);
  }

  let authorSummary = applyAuthorEntry(null, extractAuthorEntry(englishDoc.author), 'en');

  const item: GalleryItem = {
    id: String(englishDoc.id),
    slug: String(englishDoc.id),
    title: { en: englishDoc.title ?? '', zh: '' },
    description: { en: richTextToPlainText(englishDoc.description), zh: '' },
    year: englishDoc.year ?? 0,
    tags: [],
    media: normalizeMedia(englishDoc.photos),
    author: finalizeAuthor(authorSummary)
  };

  const tagMap = new Map<string, ResolvedTag>();
  applyTagsToMap(tagMap, extractTagEntries(englishDoc.tags ?? null), 'en');

  localeResults
    .filter((entry) => entry.locale !== 'en' && entry.doc)
    .forEach(({ locale, doc }) => {
      if (!doc) {
        return;
      }
      item.title[locale] = doc.title ?? item.title.en;
      item.description[locale] = richTextToPlainText(doc.description) || item.description.en;

      if (doc.photos && doc.photos.length > 0) {
        const photoMap = new Map(doc.photos.map((photo) => [photo.id, photo] as const));
        item.media = item.media.map((media) => {
          const match = photoMap.get(media.id);
          if (match?.alt) {
            return {
              ...media,
              alt: {
                ...media.alt,
                [locale]: match.alt
              }
            };
          }
          return media;
        });
      }

      applyTagsToMap(tagMap, extractTagEntries(doc.tags ?? null), locale);
      authorSummary = applyAuthorEntry(authorSummary, extractAuthorEntry(doc.author), locale);
      item.author = finalizeAuthor(authorSummary);
    });

  item.media = item.media.map((media) => ({
    ...media,
    alt: {
      en: media.alt.en,
      zh: media.alt.zh || media.alt.en
    }
  }));

  item.tags = finalizeTags(tagMap);
  item.author = finalizeAuthor(authorSummary);

  return item;
};

const loadAllTagsForLocale = async (locale: Locale): Promise<PayloadTag[]> => {
  const limit = 100;
  let page = 1;
  const collected: PayloadTag[] = [];

  while (true) {
    const { docs, nextPage } = await fetchTagsForLocale(locale, page, limit);
    collected.push(...docs);

    if (!nextPage) {
      break;
    }

    page = nextPage;
  }

  return collected;
};

export const loadTags = async (): Promise<ResolvedTag[]> => {
  const englishTags = await loadAllTagsForLocale('en');
  const localeData: Record<Locale, PayloadTag[]> = {
    en: englishTags,
    zh: []
  };

  const otherLocales = await Promise.all(
    APP_LOCALES.filter((locale) => locale !== 'en').map(async (locale) => ({
      locale,
      docs: await loadAllTagsForLocale(locale)
    }))
  );

  otherLocales.forEach(({ locale, docs }) => {
    localeData[locale] = docs;
  });

  const tagMap = new Map<string, ResolvedTag>();

  applyTagsToMap(tagMap, extractTagEntries(localeData.en), 'en');

  APP_LOCALES.filter((locale) => locale !== 'en').forEach((locale) => {
    applyTagsToMap(tagMap, extractTagEntries(localeData[locale]), locale);
  });

  return finalizeTags(tagMap);
};
